// Enhanced hijri-date-picke.component.ts
import { Component, OnInit, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import moment from 'moment-hijri';

@Component({
  selector: 'app-hijri-date-picke',
  templateUrl: './hijri-date-picke.component.html',
  styleUrls: ['./hijri-date-picke.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HijriDatePickeComponent),
      multi: true
    }
  ]
})
export class HijriDatePickeComponent implements OnInit, ControlValueAccessor {
  @Input() label: string = 'التاريخ الهجري';
  @Input() placeholder: string = 'اختر التاريخ الهجري';
  @Output() dateChange = new EventEmitter<string>();

  isOpen: boolean = false;
  hijriYear: number = 1445;
  hijriMonth: number = 0;
  hijriDay: number = 1;

  selectedDate: moment.Moment | null = null;
  gregorianDate: string = '';

  displayValue: string = '';
  disabled: boolean = false;

  // Arabic month names
  months: string[] = [
    'محرم', 'صفر', 'ربيع الأول', 'ربيع الثاني',
    'جمادى الأولى', 'جمادى الآخرة', 'رجب', 'شعبان',
    'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة'
  ];

  // Arabic days of week - full names
  daysOfWeek: string[] = [
    'الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'
  ];

  calendarDays: { day: number, isCurrentMonth: boolean, isToday: boolean, date: moment.Moment }[][] = [];

  // For ControlValueAccessor
  onChange: any = () => { };
  onTouched: any = () => { };

  constructor() { }

  ngOnInit(): void {
    const today = moment();
    this.hijriYear = parseInt(today.format('iYYYY'));
    this.hijriMonth = parseInt(today.format('iM')) - 1;
    this.generateCalendar();
  }

  // Convert Gregorian date string from API to Hijri
  @Input() set date(value: string) {
    if (value) {
      this.gregorianDate = value;
      this.selectedDate = moment(value);
      this.hijriYear = parseInt(this.selectedDate.format('iYYYY'));
      this.hijriMonth = parseInt(this.selectedDate.format('iM')) - 1;
      this.hijriDay = parseInt(this.selectedDate.format('iD'));
      this.displayValue = this.formatHijriDate(this.selectedDate);
      this.generateCalendar();
    }
  }

  // Format the Hijri date for display
  formatHijriDate(date: moment.Moment): string {
    return `${this.toArabicNumeral(parseInt(date.format('iD')))} ${this.months[parseInt(date.format('iM')) - 1]} ${this.toArabicNumeral(parseInt(date.format('iYYYY')))}`;
  }

  // Generate the calendar for the current month
  generateCalendar(): void {
    this.calendarDays = [];

    // Create a date object for the first day of the month
    const firstDay = moment().iYear(this.hijriYear).iMonth(this.hijriMonth).iDate(1);

    // Determine the first day of the week (0 = Sunday)
    const firstDayOfWeek = firstDay.day();

    // Get the number of days in the current month
    const daysInMonth = firstDay.iDaysInMonth();

    // Get the number of days in the previous month
    const prevMonth = moment(firstDay).subtract(1, 'iMonth');
    const daysInPrevMonth = prevMonth.iDaysInMonth();

    // Create the calendar array
    let week: { day: number, isCurrentMonth: boolean, isToday: boolean, date: moment.Moment }[] = [];
    let today = moment();

    // Add days from the previous month
    for (let i = 0; i < firstDayOfWeek; i++) {
      const day = daysInPrevMonth - firstDayOfWeek + i + 1;
      const date = moment(prevMonth).iDate(day);
      week.push({
        day,
        isCurrentMonth: false,
        isToday: date.isSame(today, 'day'),
        date
      });
    }

    // Add days from the current month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = moment().iYear(this.hijriYear).iMonth(this.hijriMonth).iDate(i);
      if (week.length === 7) {
        this.calendarDays.push(week);
        week = [];
      }
      week.push({
        day: i,
        isCurrentMonth: true,
        isToday: date.isSame(today, 'day'),
        date
      });
    }

    // Add days from the next month
    const nextMonth = moment(firstDay).add(1, 'iMonth');
    let dayCounter = 1;
    while (week.length < 7) {
      const date = moment(nextMonth).iDate(dayCounter);
      week.push({
        day: dayCounter++,
        isCurrentMonth: false,
        isToday: date.isSame(today, 'day'),
        date
      });
    }

    // Add the last week to the calendar
    this.calendarDays.push(week);

    // Add an additional week if needed to make 6 weeks total (for consistent UI)
    if (this.calendarDays.length < 6) {
      week = [];
      for (let i = 0; i < 7; i++) {
        const date = moment(nextMonth).iDate(dayCounter);
        week.push({
          day: dayCounter++,
          isCurrentMonth: false,
          isToday: date.isSame(today, 'day'),
          date
        });
      }
      this.calendarDays.push(week);
    }
  }

  // Previous month
  prevMonth(): void {
    this.hijriMonth++;
    if (this.hijriMonth > 11) {
      this.hijriMonth = 0;
      this.hijriYear++;
    }
    this.generateCalendar();
  }

  // Next month
  nextMonth(): void {
    this.hijriMonth--;
    if (this.hijriMonth < 0) {
      this.hijriMonth = 11;
      this.hijriYear--;
    }
    this.generateCalendar();
  }

  // Change year
  changeYear(delta: number): void {
    this.hijriYear += delta;
    this.generateCalendar();
  }

  // Select today's date
  selectToday(): void {
    const today = moment();
    this.selectDay({
      day: parseInt(today.format('iD')),
      isCurrentMonth: true,
      date: today
    });
  }

  // Select a day
  selectDay(day: { day: number, isCurrentMonth: boolean, date: moment.Moment }): void {
    this.selectedDate = day.date;
    this.hijriDay = day.day;

    // If the selected day is from a different month, update the calendar
    if (!day.isCurrentMonth) {
      this.hijriMonth = parseInt(this.selectedDate.format('iM')) - 1;
      this.hijriYear = parseInt(this.selectedDate.format('iYYYY'));
      this.generateCalendar();
    }

    this.displayValue = this.formatHijriDate(this.selectedDate);
    this.gregorianDate = this.selectedDate.format('YYYY-MM-DD') + 'T00:00:00.000Z';

    this.isOpen = false;
    this.onChange(this.gregorianDate);
    this.dateChange.emit(this.gregorianDate);
  }

  // Toggle the datepicker
  toggleDatepicker(): void {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.onTouched();
      }
    }
  }

  // Close the datepicker
  closeDatepicker(event: Event): void {
    if (!(event.target as HTMLElement).closest('.hijri-datepicker')) {
      this.isOpen = false;
    }
  }

  // Convert numbers to Arabic numerals
  toArabicNumeral(num: number): string {
    const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return num.toString().replace(/[0-9]/g, function (w) {
      return arabicNumerals[+w];
    });
  }

  // ControlValueAccessor methods
  writeValue(value: string): void {
    if (value) {
      this.date = value;
    } else {
      this.displayValue = '';
      this.selectedDate = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
